import ActionButton, {ActionButtonStyle} from "../ActionButton";
import callApplicationLead from "../../lib/callApplicationLead";
import IconButton, {IconType} from "../IconButton";
import logger from "../../lib/logger";
import Modal from "../Modal";
import React, {useRef, useState} from "react";
import RoundedCard from "../RoundedCard";
import styles from "./PersonalReferralCard.module.css";
import TextLink from "../TextLink";

const strings = {
    title: "Your Personal Referral Link:",
    share: "Share",
    confirmCopied: "Copied to Clipboard",
    copy: "Copy",
    legal:
        "By sharing my Personal Referral Link, I understand and agree that they will know that I have a 1st Financial Bank USA credit card account",
    shareDescription: "Share my personal referral link",
    email: "Email",
    sms: "SMS",
    referralLink: "My 1st Financial Bank USA Referral Link",
};

type Props = {
    // NOTE: link must be absolute, else navigator will prepend current path
    link: string;
    roundedCardClassName?: string;
    invitationCode?: string;
};

/* Abort Error: https://w3c.github.io/web-share/#share-method */
const ABORT_ERROR_CODE = 20;

export default function PersonalReferralCard(props: Props) {
    const [shareFallbackVisible, setShareFallbackVisible] = useState(false);
    const shareButtonRef = useRef<HTMLButtonElement>(null);
    const [confirmCopyVisible, setConfirmCopyVisible] = useState(false);
    const copyButtonRef = useRef<HTMLButtonElement>(null);

    function onShare() {
        if (navigator.share) {
            navigator
                .share({
                    title: strings.title,
                    url: props.link,
                })
                .catch((err) => {
                    logger.warn(err);
                    if (err.code !== ABORT_ERROR_CODE) {
                        setShareFallbackVisible(true);
                    }
                });
        } else {
            setShareFallbackVisible(true);
        }
    }

    return (
        <>
            <RoundedCard showBorder={true} className={props.roundedCardClassName}>
                <div className={styles.title}>{strings.title}</div>
                <TextLink
                    className={styles.textLink}
                    onClick={() => {
                        navigator.clipboard.writeText(props.link);
                        callApplicationLead(
                            "personal-referral-card-link-copy",
                            props.invitationCode ?? null,
                            window.location.href,
                        );
                        setConfirmCopyVisible(true);
                    }}
                    text={props.link}
                    wrapLink={true}
                />
                <div className={styles.buttons}>
                    <IconButton
                        ref={shareButtonRef}
                        className={styles.shareButton}
                        label={strings.share}
                        iconType={IconType.Share}
                        onClick={() => {
                            onShare();
                            callApplicationLead(
                                "personal-referral-card-share",
                                props.invitationCode ?? null,
                                window.location.href,
                            );
                        }}
                    />
                    <IconButton
                        ref={copyButtonRef}
                        label={strings.copy}
                        iconType={IconType.Clipboard}
                        onClick={() => {
                            navigator.clipboard.writeText(props.link);
                            callApplicationLead(
                                "personal-referral-card-copy",
                                props.invitationCode ?? null,
                                window.location.href,
                            );
                            setConfirmCopyVisible(true);
                        }}
                    />
                </div>
                <div className={styles.legal}>{strings.legal}</div>
            </RoundedCard>
            <Modal
                id="share-fallback"
                isOpen={shareFallbackVisible}
                label={strings.share}
                onClose={() => {
                    setShareFallbackVisible(false);
                    callApplicationLead(
                        "personal-referral-card-continue",
                        props.invitationCode ?? null,
                        window.location.href,
                    );
                }}
                onSubmit={() => {
                    setShareFallbackVisible(false);
                    callApplicationLead(
                        "personal-referral-card-continue",
                        props.invitationCode ?? null,
                        window.location.href,
                    );
                }}
                triggerElement={shareButtonRef.current}
            >
                <div className={styles.modalText}>{strings.shareDescription}</div>
                <div className={styles.modalButtons}>
                    <ActionButton
                        onClick={() =>
                            callApplicationLead(
                                "personal-referral-card-email",
                                props.invitationCode ?? null,
                                window.location.href,
                            )
                        }
                        className={styles.emailButton}
                        style={ActionButtonStyle.outlined}
                        label={strings.email}
                        href={`mailto:?subject=${strings.referralLink}&body=${props.link}`}
                    />
                    <ActionButton
                        onClick={() =>
                            callApplicationLead(
                                "personal-referral-card-sms",
                                props.invitationCode ?? null,
                                window.location.href,
                            )
                        }
                        className={styles.smsButton}
                        style={ActionButtonStyle.outlined}
                        label={strings.sms}
                        href={`sms:?&body=${strings.referralLink}: ${props.link}`}
                    />
                </div>
            </Modal>
            <Modal
                id="confirm-copied"
                isOpen={confirmCopyVisible}
                label={strings.copy}
                onClose={() => {
                    setConfirmCopyVisible(false);
                    callApplicationLead(
                        "confirm-copy-modal",
                        props.invitationCode ?? null,
                        window.location.href,
                    );
                }}
                onSubmit={() => {
                    setConfirmCopyVisible(false);
                    callApplicationLead(
                        "confirm-copy-modal",
                        props.invitationCode ?? null,
                        window.location.href,
                    );
                }}
                triggerElement={copyButtonRef.current}
            >
                <div className={styles.modalText}>{strings.confirmCopied}</div>
            </Modal>
        </>
    );
}
